import React, { useState } from 'react';
import { Container, Box, Typography, TextField, InputAdornment, Card, CardActionArea, CircularProgress, Alert, Avatar, Chip, Button } from '@mui/material';
import { Search, CheckCircle, ArrowForward, AccountBalance, NewReleases, Description } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCategories } from '../hooks/useCategories';
import { useSchemes } from '../hooks/useSchemes';
import { useLanguage } from '../contexts/LanguageContext';
import { getLocalizedName } from '../models/Category';
import { getLocalizedTitle, getLocalizedDescription } from '../models/Scheme';
import { getIconComponent } from '../utils/iconMapping';

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const { data: categories, isLoading, error } = useCategories();
  const { data: schemes, isLoading: schemesLoading } = useSchemes();

  const filteredCategories = categories?.filter(cat =>
    getLocalizedName(cat, currentLanguage).toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get latest 3 schemes (new or recently updated)
  const latestSchemes = schemes?.slice(0, 2);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>       
      <TextField
        fullWidth
        placeholder={t('search_placeholder')}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
      />
            {/* Latest Government Schemes Section */}
      {!schemesLoading && latestSchemes && latestSchemes.length > 0 && (
        <Box sx={{ mt: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <Avatar sx={{ bgcolor: '#EDE7F6', color: '#673AB7', mr: 2, width: 38, height: 38 }}>
              <NewReleases />
            </Avatar>
            <Typography variant="h4" component="h3">
              {t('latest_schemes')}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {latestSchemes.map((scheme) => (
              <Card 
                key={scheme.id}
                elevation={2}
                sx={{ 
                  bgcolor: '#F3E8FF',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(103, 58, 183, 0.15)',
                  }
                }}
              >
                <CardActionArea 
                  onClick={() => navigate(`/scheme/${scheme.id}`)}
                  sx={{ p: { xs: 2.5, sm: 3 } }}
                >
                  <Box sx={{ display: 'flex', gap: 2 }}>         
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" component="h4" gutterBottom sx={{ mb: 1.5 }}>
                        {getLocalizedTitle(scheme, currentLanguage)}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                        {scheme.category && (
                          <Chip 
                            label={scheme.category.toUpperCase()} 
                            size="small"
                            sx={{ 
                              bgcolor: '#673AB7',
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.7rem'
                            }}
                          />
                        )}
                        {scheme.isNew && (
                          <Chip 
                            icon={<NewReleases sx={{ fontSize: 16, color: 'white !important' }} />}
                            label={t('new_label')} 
                            size="small"
                            sx={{ 
                              bgcolor: '#43A047',
                              color: 'white',
                              fontWeight: 600,
                              fontSize: '0.7rem'
                            }}
                          />
                        )}
                      </Box>
                      
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          mb: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {getLocalizedDescription(scheme, currentLanguage)}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircle sx={{ color: '#43A047', fontSize: 20 }} />
                          <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            {t('benefits_available')}
                          </Typography>
                        </Box>
                        <ArrowForward sx={{ color: '#673AB7' }} />
                      </Box>
                    </Box>
                  </Box>
                </CardActionArea>
              </Card>
            ))}
          </Box>

          <Button
            fullWidth
            variant="text"
            endIcon={<ArrowForward />}
            onClick={() => navigate('/schemes')}
            sx={{
              mt: 3,
              color: '#673AB7',
              fontWeight: 600,
              fontSize: '1rem',
              py: 1.5,
              '&:hover': {
                bgcolor: 'rgba(103,58,183,0.04)'
              }
            }}
          >
            {t('view_all_schemes')}
          </Button>
        </Box>
      )}
      
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
      <Avatar sx={{ bgcolor: '#EDE7F6', color: '#673AB7', mr: 2, width: 38, height: 38 }}>
        <Description />
      </Avatar>
      <Typography variant="h4" component="h3">
        {t('categories')}
      </Typography>
    </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('error_occurred')}
        </Alert>
      )}
      
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, 
          gap: 2
        }}>
          {filteredCategories?.map((category) => (
            <Card 
              key={category.id}
              elevation={0}
              sx={{ 
                bgcolor: '#EDE7F6',
                borderRadius: 3,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 6px 16px rgba(103, 58, 183, 0.2)',
                }
              }}
            >
              <CardActionArea 
                onClick={() => navigate(`/document/${category.id}`)}
                sx={{ 
                  p: 3, 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  minHeight: 180 
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: 'rgba(103,58,183,0.12)',
                    width: 80,
                    height: 80,
                    mb: 2
                  }}
                >
                  <Box sx={{ color: '#673AB7' }}>
                    {getIconComponent(category.icon)}
                  </Box>
                </Avatar>
                <Typography variant="h6" component="h3" color="text.primary" textAlign="center">
                  {getLocalizedName(category, currentLanguage)}
                </Typography>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      )}
      
      {!isLoading && filteredCategories?.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            {t('no_results')}
          </Typography>
        </Box>
      )}


    </Container>
  );
};
